import { selectCategoriesIds } from 'app/main/apps/categories/ducks/categories.selector';
import {
  CLIENTS_PARTNER_DEFAULT,
  CLIENTS_SORT_DEFAULT,
  CLIENTS_SORT_ORDER_DEFAULT,
  CLIENTS_STATUS_DEFAULT,
  PAGE,
  actions as storesActions,
} from 'app/main/apps/stores/ducks/stores.slice';
import { ClientsSortsUrlEnum } from 'app/main/pages/clients/type';
import { AppDispatch, RootState } from 'app/store';
import { MerchantListFilters } from '../types/merchantListFilters';
import { MerchantAndStoreUrlEnum } from '../types/type';
import { dropUnknownCategory, parseMerchantListFilters, writeMerchantListFilters } from '../utils/merchantFilterParams';
import { selectMerchantListFilters } from './merchantListFilters.selector';
import {
  mergeMerchantListFilters,
  resetMerchantListFilters,
  setMerchantListFilter,
  setMerchantListFilters,
} from './merchantListFilters.slice';

export const initMerchantListFilters = () => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  const filters = parseMerchantListFilters(url.searchParams);
  writeMerchantListFilters(url, filters);
  window.history.replaceState({}, '', url.toString());
  dispatch(setMerchantListFilters(filters));
};

// The category referential loads after the URL is read, so an unknown id can only be dropped at fetch time.
export const pruneUnknownMerchantCategory =
  () =>
  (dispatch: AppDispatch, getState: () => RootState): MerchantListFilters => {
    const filters = selectMerchantListFilters(getState());
    const knownFilters = dropUnknownCategory(filters, selectCategoriesIds(getState()).map(String));
    if (knownFilters !== filters) {
      const url = new URL(window.location.href);
      writeMerchantListFilters(url, knownFilters);
      window.history.replaceState({}, '', url.toString());
      dispatch(setMerchantListFilters(knownFilters));
    }
    return knownFilters;
  };

const pushFiltersOnFirstPage = (dispatch: AppDispatch, nextFilters: MerchantListFilters) => {
  const url = new URL(window.location.href);
  writeMerchantListFilters(url, nextFilters);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());
  dispatch(storesActions.setMerchantAndStoresPagination({ page: PAGE }));
};

export const changeMerchantListFilter =
  <K extends keyof MerchantListFilters>(key: K, value: MerchantListFilters[K]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    pushFiltersOnFirstPage(dispatch, { ...selectMerchantListFilters(getState()), [key]: value });
    dispatch(setMerchantListFilter({ key, value }));
  };

export const changeMerchantListFilters =
  (changes: MerchantListFilters) => (dispatch: AppDispatch, getState: () => RootState) => {
    pushFiltersOnFirstPage(dispatch, { ...selectMerchantListFilters(getState()), ...changes });
    dispatch(mergeMerchantListFilters(changes));
  };

export const resetAllMerchantFilters = (setTempSearchText: (text: string) => void) => (dispatch: AppDispatch) => {
  const url = new URL(window.location.href);
  writeMerchantListFilters(url, {});
  url.searchParams.delete(MerchantAndStoreUrlEnum.search);
  url.searchParams.set(MerchantAndStoreUrlEnum.status, CLIENTS_STATUS_DEFAULT);
  url.searchParams.set(MerchantAndStoreUrlEnum.partner, CLIENTS_PARTNER_DEFAULT);
  url.searchParams.set(ClientsSortsUrlEnum.sort, CLIENTS_SORT_DEFAULT);
  url.searchParams.set(ClientsSortsUrlEnum.order, CLIENTS_SORT_ORDER_DEFAULT);
  url.searchParams.set(MerchantAndStoreUrlEnum.page, PAGE.toString());
  window.history.pushState({}, '', url.toString());

  setTempSearchText('');
  dispatch(resetMerchantListFilters());
  dispatch(storesActions.setMerchantAndStoresFilter(''));
  dispatch(storesActions.setMerchantAndStoresStatusFilter(CLIENTS_STATUS_DEFAULT));
  dispatch(storesActions.setMerchantAndStoresPartnerFilter(CLIENTS_PARTNER_DEFAULT));
  dispatch(storesActions.setMerchantAndStoresSort({ type: CLIENTS_SORT_DEFAULT, order: CLIENTS_SORT_ORDER_DEFAULT }));
  dispatch(storesActions.setMerchantAndStoresPagination({ page: PAGE }));
};
